import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import {
  ReactFlow,
  MiniMap,
  Controls,
  useReactFlow,
  ReactFlowProvider,
  useOnSelectionChange,
  getIncomers,
  getOutgoers,
  getConnectedEdges,
  reconnectEdge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Box, CircularProgress, Stack, useTheme } from "@mui/material";
import NodeConfigurationPanel from "./node-configuration-panel";
import NodePickerPanel from "./node-picker-panel";
import { workflowService } from "../../../../Core/Api/services";
import { useSelector } from "react-redux";
import {
  AddDiagramEdge,
  AddDiagramNode,
  AddEdge,
  GetAutoLayoutTrigger,
  GetDiagramEdges,
  GetDiagramNodes,
  GetFitViewTrigger,
  GetLoadingWorkflow,
  GetNodes,
  GetOrientation,
  GetSelectedNodeType,
  GetWorkflow,
  InitWorkflowDesigner,
  LoadModelFromExec,
  ProcessAutoLayout,
  SetDiagramEdges,
  SetLoadingNodes,
  SetLoadingWorkflow,
  SetNodes,
  SetSelectedEdges,
  SetSelectedNodes,
  SetSelectedNodeType,
  SetWorkflows,
  UpdateEdges,
  UpdateNodes,
} from "../../../../Redux/Reducers/redux-slice-workflow-designer";
import { KalikoNode, KalikoNodeProps } from "./kaliko-node";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { DnDProvider, useDnD } from "./DnDContext";
import CustomEdge from "./edge";
import { DbKalikoWorkflowExecInfos, KalikoNodeStatus } from "../../../../Core/Api/Types/kaliko";
import { ModalContext } from "../../../../Components/Modals";
import ModalLoadWorkflowDiagramDesigner, {
  ModalLoadWorkflowDiagramDesignerElementProps,
} from "../../../../Components/Modals/ModalLoadWorkflowDiagramDesigner/modal-load-workflow-diagram-designer";
import Elk, { ELK } from "elkjs/lib/elk.bundled.js";
import "./hide-attribution.css";

const nodeOrigin: [number, number] = [0.5, 0];
const defaultEdgeOptions = {
  type: "custom",
  markerEnd: "edge-circle",
};
interface DiagramEditorProps {
  workflowId?: number;
  exec?: DbKalikoWorkflowExecInfos;
}
function DiagramEditor(props: DiagramEditorProps) {
  const { workflowId, exec } = props;
  const dispatch = useDispatch();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const theme = useTheme();

  const editable = workflowId === undefined;

  const { screenToFlowPosition, fitView, getNodes } = useReactFlow();

  const nodeTypes = useMemo(() => ({ kalikoNode: KalikoNode }), []);
  const edgeTypes = {
    custom: CustomEdge,
  };

  const reactFlowWrapper = useRef<any>(null);
  const [type] = useDnD();
  const elkRef = useRef<ELK>(new Elk());

  const selectedNodeType = useSelector(GetSelectedNodeType);
  const kalikoNodeTypes = useSelector(GetNodes);
  const diagramNodes = useSelector(GetDiagramNodes);
  const diagramEdges = useSelector(GetDiagramEdges);
  const loadingWorkflow = useSelector(GetLoadingWorkflow);
  const workflow = useSelector(GetWorkflow);
  const fitViewTrigger = useSelector(GetFitViewTrigger);
  const autoLayoutTrigger = useSelector(GetAutoLayoutTrigger);
  const orientation = useSelector(GetOrientation);

  useEffect(() => {
    setTimeout(() => {
      fitView();
    }, 250);
  }, [workflow?.id, workflowId, fitViewTrigger, orientation]);

  useEffect(() => {
    setTimeout(() => {
      calculateAutoLayout();
    }, 250);
  }, [autoLayoutTrigger]);

  const calculateAutoLayout = () => {
    const elkInstance = elkRef.current;

    elkInstance
      .layout({
        id: "root",
        layoutOptions: { "elk.algorithm": "layered" },
        children: [
          ...diagramNodes.map((n) => ({
            id: n.id,
            width: orientation === "horizontal" ? 200 : 100,
            height: orientation === "horizontal" ? 100 : 200,
          })),
        ],
        edges: [
          ...diagramEdges
            .filter((e) => diagramNodes.find((n) => n.id === e.target) && diagramNodes.find((n) => n.id === e.source))
            .map((e) => ({ id: e.id, sources: [e.source], targets: [e.target] })),
        ],
      })
      .then((response) => {
        if (response.children) dispatch(ProcessAutoLayout(response.children));
      })
      .catch((err) => {
        console.log("err elk", err);
      });
  };

  const onChange = useCallback(
    ({ nodes, edges }) => {
      dispatch(SetSelectedNodes(nodes.map((node) => node.id)));
      dispatch(SetSelectedEdges(edges.map((edge) => edge.id)));
    },
    [loadingWorkflow]
  );

  useOnSelectionChange({
    onChange,
  });

  useEffect(() => {
    dispatch(SetLoadingNodes(true));
    editable && dispatch(SetLoadingWorkflow(true));
    workflowService.GetNodes().then((response) => {
      dispatch(SetLoadingNodes(false));
      if (response) dispatch(SetNodes(response));
    });

    editable &&
      workflowService.GetWorkflowModels().then((response) => {
        dispatch(SetLoadingWorkflow(false));
        if (response && response.length > 0) {
          dispatch(SetWorkflows(response));
        }
      });

    return () => {
      dispatch(InitWorkflowDesigner());
    };
  }, []);

  useEffect(() => {
    if (exec && exec.model) {
      dispatch(LoadModelFromExec(exec));
    }
  }, [exec]);

  const onConnectEnd = useCallback(
    (event, connectionState) => {
      if (!connectionState?.isValid) {
        const id = uuidv4();
        const { clientX, clientY } = "changedTouches" in event ? event.changedTouches[0] : event;

        if (!selectedNodeType) return;

        const newNodeData = kalikoNodeTypes.find((x) => x.type === selectedNodeType);
        const currentTypeCount = getNodes().filter((x) => x.data.type === type)?.length;
        const newNode: KalikoNodeProps = {
          id,
          position: screenToFlowPosition({
            x: clientX,
            y: clientY,
          }),
          data: {
            ...newNodeData,
            uid: id,
            name: newNodeData?.systemName + (currentTypeCount ? " " + (currentTypeCount + 1).toString() : ""),
          },
          origin: [0.5, 0.0],
          type: "kalikoNode",
        };
        dispatch(AddDiagramNode(newNode));

        const newEdge = {
          id,
          source: connectionState.fromNode.id,
          target: id,
          className: "",
          data: { condition: null, name: "" },
        };
        dispatch(AddDiagramEdge(newEdge));
      }
    },
    [screenToFlowPosition, selectedNodeType, kalikoNodeTypes, diagramNodes?.length]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      if (!type) {
        return;
      }

      const id = uuidv4();

      const newNodeData = kalikoNodeTypes.find((x) => x.type === (type as any));
      const currentTypeCount = diagramNodes.filter((x) => x.data.type === type)?.length;

      const newNode: KalikoNodeProps = {
        id,
        position: screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        }),
        data: {
          ...newNodeData,
          status: KalikoNodeStatus.Pending,
          inputParams: newNodeData?.defaultInputParams,
          uid: id,
          name: newNodeData?.systemName + (currentTypeCount ? " " + (currentTypeCount + 1).toString() : ""),
        },
        origin: [0.5, 0.0],
        type: "kalikoNode",
      };
      dispatch(AddDiagramNode(newNode));
    },
    [screenToFlowPosition, type, kalikoNodeTypes, diagramNodes?.length]
  );

  const onNodesChange = (changes) => {
    dispatch(UpdateNodes(changes));
  };

  const onEdgesChange = (changes) => {
    dispatch(UpdateEdges(changes));
  };

  const onConnect = useCallback(
    (params) =>
      diagramNodes
        .filter(
          (node) => node.id === params.source || (node.selected && diagramNodes.filter((n) => n.selected).length > 1)
        )
        .forEach((x) => {
          dispatch(AddEdge({ ...params, source: x.id, data: { condition: null, name: "" } }));
        }),
    [diagramNodes]
  );

  const onReconnect = useCallback(
    (oldEdge, newConnection) => dispatch(SetDiagramEdges(reconnectEdge(oldEdge, newConnection, diagramEdges))),
    [diagramEdges]
  );
  const onNodesDelete = useCallback(
    (deleted) => {
      dispatch(
        SetDiagramEdges(
          deleted.reduce((acc, node) => {
            const incomers = getIncomers(node, diagramNodes, diagramEdges);
            const outgoers = getOutgoers(node, diagramNodes, diagramEdges);
            const connectedEdges = getConnectedEdges([node], diagramEdges);

            const remainingEdges = acc.filter((edge) => !connectedEdges.includes(edge));

            const createdEdges = incomers.flatMap(({ id: source }) =>
              outgoers.map(({ id: target }) => ({
                id: `${source}->${target}`,
                source,
                target,
              }))
            );

            return [...remainingEdges, ...createdEdges];
          }, diagramEdges)
        )
      );
    },
    [diagramNodes, diagramEdges]
  );

  const heightWrapper = editable ? "calc(100vh - 184px)" : "calc(100vh - 320px)";
  const height = editable ? "calc(100% + 70px)" : "100%";

  const handleClick = () => {
    dispatch(SetSelectedNodeType(null));
  };
  return (
    <>
      {editable && <NodePickerPanel />}
      <div className="reactflow-wrapper" ref={reactFlowWrapper}>
        <Box minHeight={"400px"} width={"100%"} height={heightWrapper}>
          {loadingWorkflow ? (
            <Stack minHeight={"400px"} width={"100%"} height={height} justifyContent="center" alignItems={"center"}>
              <CircularProgress />
            </Stack>
          ) : (
            <Box minHeight={"400px"} width={"100%"} height={height}>
              <ReactFlow
                nodesDraggable={editable}
                nodesConnectable={editable}
                elementsSelectable={editable}
                nodes={diagramNodes}
                edges={diagramEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodesDelete={onNodesDelete}
                onConnect={onConnect}
                nodeTypes={nodeTypes}
                onConnectEnd={onConnectEnd}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onClick={handleClick}
                colorMode={theme.palette.mode}
                style={{
                  backgroundColor: theme.palette.background.page,
                }}
                edgeTypes={edgeTypes}
                defaultEdgeOptions={defaultEdgeOptions}
                edgesFocusable
                fitView
                onReconnect={onReconnect}
                fitViewOptions={{ padding: 2 }}
                nodeOrigin={nodeOrigin}
              >
                {editable && <MiniMap />}
                {editable && <Controls showInteractive={editable} />}
              </ReactFlow>
            </Box>
          )}
        </Box>
      </div>
      {editable && <NodeConfigurationPanel />}
    </>
  );
}

function SettingsDiagramDesigner(props) {
  return (
    <ReactFlowProvider>
      <DnDProvider>
        <DiagramEditor {...props} />
      </DnDProvider>
    </ReactFlowProvider>
  );
}

export default SettingsDiagramDesigner;
